import { decodeToken } from "react-jwt";
import api from "../services/api";

// Super-admin "enter tenant" (impersonation), as a SELF-CONTAINED client-side
// scoped session.
//
// The impersonation access token (30-min, carries an `impersonatedBy` claim) is
// stored in localStorage["token"] like any session. useAuth is made
// impersonation-aware so it NEVER calls /auth/refresh_token while impersonating:
// a refresh would use the admin's still-valid httpOnly jrt cookie and silently
// mint an ADMIN token back, re-elevating to super-admin and defeating the 30-min
// bound. Because the admin jrt cookie is never touched, "exit" is a clean restore
// of the backed-up admin token.
//
// The backup lives in sessionStorage (dies with the tab), and impersonation is
// keyed off the ACTIVE token's impersonatedBy claim — never off the mere presence
// of the backup key — so a stale backup can never re-summon admin rights for
// whoever logs in next (no shared-browser privilege escalation).
const ADMIN_TOKEN_BACKUP = "impersonatorToken";
const IMPERSONATION_NAME = "impersonationCompanyName";

const parseStoredToken = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return raw;
  }
};

const activePayload = () => {
  const jwt = parseStoredToken(localStorage.getItem("token"));
  if (!jwt) return null;
  try {
    return decodeToken(jwt);
  } catch (e) {
    return null;
  }
};

// True only when the CURRENTLY ACTIVE token is an impersonation token.
export function isImpersonating() {
  const p = activePayload();
  return !!(p && p.impersonatedBy);
}

// Minimal user object hydrated from the impersonation token, used on a hard
// reload during impersonation (when we must NOT refresh to fetch the user).
export function decodeImpersonatedUser() {
  const p = activePayload();
  if (!p) return null;
  return {
    id: p.id,
    name: p.username,
    profile: p.profile,
    companyId: p.companyId,
    super: false,
  };
}

export function impersonatedCompanyName() {
  return sessionStorage.getItem(IMPERSONATION_NAME) || "this tenant";
}

export function clearImpersonationBackup() {
  sessionStorage.removeItem(ADMIN_TOKEN_BACKUP);
  sessionStorage.removeItem(IMPERSONATION_NAME);
}

export async function impersonateCompany(companyId) {
  const { data } = await api.post(`/api/admin/companies/${companyId}/impersonate`);

  const adminToken = localStorage.getItem("token");
  if (adminToken) sessionStorage.setItem(ADMIN_TOKEN_BACKUP, adminToken);
  if (data.company && data.company.name) {
    sessionStorage.setItem(IMPERSONATION_NAME, data.company.name);
  }

  // Match useAuth's storage contract exactly (token JSON-stringified; header set
  // per-request from localStorage). Full reload so the whole SPA (socket,
  // react-query cache, providers) re-boots cleanly under the tenant.
  localStorage.setItem("token", JSON.stringify(data.token));
  window.location.href = "/";
}

export function exitImpersonation() {
  const adminToken = sessionStorage.getItem(ADMIN_TOKEN_BACKUP);
  clearImpersonationBackup();
  if (adminToken) {
    // The admin jrt cookie was never touched, so restoring the admin access
    // token resumes a fully valid super-admin session.
    localStorage.setItem("token", adminToken);
    window.location.href = "/admin";
  } else {
    // No backup (e.g. the tab was closed and reopened) — cannot safely restore
    // admin; end the session and go to login rather than leave a dangling state.
    localStorage.removeItem("token");
    window.location.href = "/login";
  }
}
